import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {useRequestsTable} from 'src/modules/vendor-admin/hooks/useRequestsTable';
import {
    selectIsLoadingRequests,
    selectRequests,
    selectRequestsTotalResults,
} from 'src/modules/vendor-admin/service/requests.slice';
import {
    ApprovalRequestSearchCriteria,
    ApprovalRequestSearchCriteriaSortOrder,
} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';
import {User} from 'src/openapi/admin/models/User';

import {RequestsLoadingState} from '../components/requests-loading-state';
import {RequestsTable} from '../components/requests-table';
import {ToolsRibbonComponent} from '../components/tools-ribbon-component';
import {RequestTopFilters} from '../filters/request-top-filters';

type Props = {
    criteria: ApprovalRequestSearchCriteria,
    onUpdateCriteria: (criteria: Partial<ApprovalRequestSearchCriteria>) => void,
    currentUser: User | null,
};

export const AllRequestsTabContent = (props: Props) => {
    const {criteria, onUpdateCriteria, currentUser} = props;
    const hasActiveFilter = !!(
        criteria.email
        || criteria.firstName
        || criteria.lastName
        || criteria.vendorId
        || (criteria.requestStatuses || []).length
    );
    const requests = useSelector(selectRequests);

    const isLoadingRequests = useSelector(selectIsLoadingRequests);
    const requestsTotalCount = useSelector(selectRequestsTotalResults);
    const {selectedIds, clearAll, selectPage} = useRequestsTable(requests);
    const pageSelectedCount = requests.reduce(
        (count, request) => count + (selectedIds.includes(request.requestId) ? 1 : 0),
        0,
    );

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        const newCriteria: Partial<ApprovalRequestSearchCriteria> = {
            requestStatuses: [],
            page: 1,
            pageSize: 25,
            sortOrder: ApprovalRequestSearchCriteriaSortOrder.DESC,
        };
        if (currentUser?.vendor?.id) {
            newCriteria.vendorId = currentUser.vendor.id;
        }
        onUpdateCriteria(newCriteria);
    }, [onUpdateCriteria, currentUser]);

    return (
        <>
            <RequestTopFilters
                criteria={criteria}
                onChange={onUpdateCriteria}
                currentUser={currentUser}
            />
            <ToolsRibbonComponent
                pageIds={requests.map(r => r.requestId)}
                selectedIds={selectedIds}
                pageSelectedCount={pageSelectedCount}
                onSelectPage={selectPage}
                onClear={clearAll}
            />
            {isLoadingRequests && !requests.length ? (
                <RequestsLoadingState/>
            ) : (
                <>
                    <RequestsTable
                        requests={requests}
                        criteria={criteria}
                        onUpdateCriteria={onUpdateCriteria}
                        showStatusColumn={true}
                        hasActiveFilter={hasActiveFilter}
                        noResultsMessage="There are currently no requests"
                        totalCount={requestsTotalCount}
                    />
                </>
            )}
        </>
    );
};
